import { Typography, Input, Button, Avatar, Spin, Tag, Tooltip, Descriptions, Tour, TourProps, Progress } from "antd";
import { usePermissions } from "./PermissionsProvider";
import './ChatComponent.css'; // Import CSS for styling
import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { SandboxMessage, SandboxSession, SandboxApplication } from "./model_gen/sandbox";
import { UserOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Application } from "./model_gen/application";
import { SandboxService } from "./services/SandboxService";
import { EvaluateResponse, EvaluateResult } from "./model_gen/evaluation";
import { Sentinel } from "./model_gen/sentinel";
import MaitaiIcon from "./assets/maitai_icon_filled.svg";
import MetaLogo from "./assets/meta_primary.svg";

interface SyncChatComponentProps {
    chosenApplication: SandboxApplication | undefined;
    mappedApplication: Application | undefined;
    evalResponses: EvaluateResponse[];
    setEvalResponses: React.Dispatch<React.SetStateAction<EvaluateResponse[]>>;
    evalResponsesRef: React.MutableRefObject<EvaluateResponse[]>;
    ref: any;
    models: string[];
    temperature: number;
    evaluations: boolean;
    autocorrect: boolean;
    retrieveContext: boolean;
    streaming: boolean;
    targetMessage: SandboxMessage | null;
    setTargetMessage: React.Dispatch<React.SetStateAction<SandboxMessage | null>>;
    openRightSider?: () => void;
}

interface Conversation {
    turns: Turn[];
}

interface Turn {
    sender: "USER" | "BOT";
    messages: SandboxMessage[];
}

interface ModelTiming {
    model: string;
    startTime: number;
    ttftTime: number | null;
    ttftValue: number | null;
    completionTime: number | null;
    inferenceTime: number | null;
}

interface TurnModelTimings {
    turnIndex: number;
    modelTimings: ModelTiming[];
}

const SyncChatComponent: React.FC<SyncChatComponentProps> = forwardRef(({ chosenApplication, mappedApplication, evalResponses, setEvalResponses, evalResponsesRef, models, temperature, evaluations, autocorrect, retrieveContext, streaming, targetMessage, setTargetMessage, openRightSider }, ref) => {
    const { apiKey } = usePermissions();
    const [conversation, setConversation] = useState<Conversation>({ turns: [] });
    const conversationRef = useRef<Conversation>({ turns: [] });
    const [inputValue, setInputValue] = useState('');
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(false);
    const [awaitingFirstMessage, setAwaitingFirstMessage] = useState(false);
    const [session, setSession] = useState<SandboxSession | undefined>(undefined);
    const [strobeMessage, setStrobeMessage] = useState<{ id: string | null, color: string }>({ id: null, color: '' });
    const severityColor: Record<number, string> = {
        1: "default", // INFO
        2: "warning", // WARN
        3: "error"    // ERROR
    };
    const faultRef = useRef<any>(null);
    const [tourOpen, setTourOpen] = useState(false);
    const [tourShown, setTourShown] = useState(false);
    const tourSteps: TourProps['steps'] = [
        {
            title: 'FAULT FOUND!',
            description: 'Maitai found a fault!\nHover over the fault badge to see the description and proposed corrections.',
            target: () => faultRef.current,
            nextButtonProps: {
                children: 'Okay',
                onClick: () => {
                    setTourOpen(false);
                }
            }
        }
    ];
    const [isTyping, setIsTyping] = useState(false);
    const processedTokenRefs = useRef<Record<string, boolean>>({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [modelTimings, setModelTimings] = useState<ModelTiming[]>([]);
    const [allTurnTimings, setAllTurnTimings] = useState<TurnModelTimings[]>([]);
    const modelTimingsRef = useRef<ModelTiming[]>([]);
    const [animationFrame, setAnimationFrame] = useState<number | null>(null);
    const [currentTime, setCurrentTime] = useState<number>(Date.now());

    // Model color mapping for consistency
    const modelColorMap: Record<string, string> = {
        "gpt-3.5-turbo": "#10a37f",
        "gpt-4": "#8e44ad",
        "gpt-4-turbo": "#9b59b6",
        "claude-3-opus": "#e67e22",
        "claude-3-sonnet": "#d35400",
        "claude-3-haiku": "#e74c3c",
        "mistral-medium": "#3498db",
        "mistral-small": "#2980b9",
        "maitai:llama-3.1-8b": "#21b892",
        "maitai:red_robin": "#21b892",
        "llama-3.1-8b": "#21b892",
        "red_robin": "#21b892",
        "openai:gpt-4o-mini": "#ffa658",
        "gpt-4o-mini": "#ffa658",
        "maitai:predibase-llama-3.1-8b-instruct": "#ffa658",
        "predibase-llama-3.1-8b-instruct": "#ffa658",
    };
    
    // Model label mapping for display
    const modelLabelMap: Record<string, React.ReactNode> = {
        "maitai:red_robin": (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                maitai-groq-lora
                <img src={MetaLogo} alt="Meta" style={{ height: '20px', marginLeft: '4px' }} />
            </span>
        ),
        "maitai:predibase-llama-3.1-8b-instruct": (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                llama-3.1-8b
                <img src={MetaLogo} alt="Meta" style={{ height: '20px', marginLeft: '4px' }} />
            </span>
        ),
        "openai:gpt-4o-mini": "gpt-4o-mini",
    };
    
    // Get color for a model (with fallback)
    const getModelColor = (modelName: string): string => {
        const baseModelName = modelName?.toLowerCase().split(":").pop() || "";
        // Try exact match first
        if (baseModelName && modelColorMap[baseModelName]) {
            return modelColorMap[baseModelName];
        }
        // Try partial match
        const matchingModel = Object.keys(modelColorMap).find(model => 
            baseModelName.includes(model)
        );
        return matchingModel ? modelColorMap[matchingModel] : "#666666"; // Default gray
    };

    // Get user-friendly label for a model
    const getModelLabel = (modelName: string): React.ReactNode => {
        // If there's a direct mapping, use it
        if (modelName && modelLabelMap[modelName]) {
            return modelLabelMap[modelName];
        }
        // Otherwise fallback to extracting the model name from the string
        return modelName?.split(":").pop() || modelName || "";
    };
    
    // Function to determine timing color with separate thresholds for TTFT and inference time
    const getTimingColor = (time: number, isTTFT: boolean = false): string => {
        if (isTTFT) {
            // TTFT thresholds
            if (time < 300) return "#52c41a"; // Fast - green
            if (time < 400) return "#faad14"; // Medium - yellow
            return "#f5222d"; // Slow - red
        } else {
            // Inference time thresholds
            if (time <= 500) return "#52c41a"; // Fast - green
            if (time <= 650) return "#faad14"; // Medium - yellow
            return "#f5222d"; // Slow - red
        }
    };

    // Function to update current time for animation
    const updateCurrentTime = useCallback(() => {
        setCurrentTime(Date.now());
        const frame = requestAnimationFrame(updateCurrentTime);
        setAnimationFrame(frame);
    }, []);
    
    // Start/stop animation when model timings change
    useEffect(() => {
        modelTimingsRef.current = modelTimings;
        
        // If we have active timings (not all completionTime are set), start animation
        const hasActiveTimings = modelTimings.length > 0 && modelTimings.some(t => t.completionTime === null);
        
        if (hasActiveTimings && !animationFrame) {
            const frame = requestAnimationFrame(updateCurrentTime);
            setAnimationFrame(frame);
        } else if (!hasActiveTimings && animationFrame) {
            cancelAnimationFrame(animationFrame);
            setAnimationFrame(null);
        }
        
        // Cleanup animation frame on unmount
        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [modelTimings, animationFrame, updateCurrentTime]);
    
    // Effect to store turn timings when a turn is complete
    useEffect(() => {
        // Only add to allTurnTimings if we have completed timings
        if (modelTimings.length > 0 && modelTimings.some(timing => 
            timing.ttftValue !== null || timing.ttftTime !== null || timing.inferenceTime !== null
        )) {
            // Find the current turn index
            const currentTurnIndex = conversation.turns.length - 1;
            
            // Check if this turn already has timings
            const existingTurnIndex = allTurnTimings.findIndex(t => t.turnIndex === currentTurnIndex);
            
            if (existingTurnIndex >= 0) {
                // Update existing turn timings
                setAllTurnTimings(prev => {
                    const newTimings = [...prev];
                    newTimings[existingTurnIndex] = {
                        turnIndex: currentTurnIndex,
                        modelTimings: [...modelTimings]
                    };
                    return newTimings;
                });
            } else {
                // Add new turn timings
                setAllTurnTimings(prev => [
                    ...prev, 
                    {
                        turnIndex: currentTurnIndex,
                        modelTimings: [...modelTimings]
                    }
                ]);
            }
        }
    }, [modelTimings, conversation.turns.length]);
    
    // Helper function to get model segments for a specific set of timings
    const getModelSegmentsForTimings = useCallback((turnModelTimings: ModelTiming[]) => {
        // Sort models by TTFT or inference time based on streaming setting
        const sortedTimings = [...turnModelTimings].sort((a, b) => {
            // If streaming is on, prioritize TTFT
            if (streaming) {
                // If we have TTFT values from metadata, use those for sorting
                if (a.ttftValue !== null && b.ttftValue !== null) {
                    return a.ttftValue - b.ttftValue;
                }
                
                // If one has TTFT value and the other doesn't, prioritize the one with data
                if (a.ttftValue !== null) return -1;
                if (b.ttftValue !== null) return 1;
                
                // Fall back to calculated TTFT if available
                if (a.ttftTime !== null && b.ttftTime !== null) {
                    return (a.ttftTime - a.startTime) - (b.ttftTime - b.startTime);
                }
                
                // If one has calculated TTFT and the other doesn't, prioritize the one with data
                if (a.ttftTime !== null) return -1;
                if (b.ttftTime !== null) return 1;
            } 
            // If streaming is off, use inference times
            else {
                // If we have inference times, use those for sorting
                if (a.inferenceTime !== null && b.inferenceTime !== null) {
                    return a.inferenceTime - b.inferenceTime;
                }
                
                // If one has inference time and the other doesn't, prioritize the one with data
                if (a.inferenceTime !== null) return -1;
                if (b.inferenceTime !== null) return 1;
            }
            
            // Fall back to completion time if available
            const aTime = a.completionTime || currentTime;
            const bTime = b.completionTime || currentTime;
            return (aTime - a.startTime) - (bTime - b.startTime);
        });
        
        // Calculate max time across all models (default minimum 500ms)
        const maxTimeValue = Math.max(
            500, // Minimum scale
            ...sortedTimings.map(timing => {
                // Use TTFT from metadata if streaming is on and value is available
                if (streaming && timing.ttftValue !== null) {
                    return timing.ttftValue;
                }
                // Fall back to calculated TTFT if available
                else if (streaming && timing.ttftTime !== null) {
                    return timing.ttftTime - timing.startTime;
                }
                // Otherwise, use inference time if available
                else if (!streaming && timing.inferenceTime !== null) {
                    return timing.inferenceTime;
                }
                // For incomplete responses, use current elapsed time
                return currentTime - timing.startTime;
            })
        );
        
        // Calculate total max time for scaling
        const maxTimeMs = maxTimeValue; // Use the dynamically calculated max
        
        // Calculate segments - adjust to create a timeline view
        let lastEndTime = 0;
        const segments = sortedTimings.map((timing, index) => {
            let duration: number;
            let displayDuration: number; // What's shown in the UI
            let isComplete: boolean;
            let startPercent = 0;
            let widthPercent = 0;
            
            // Determine which metric to use based on streaming setting
            if (streaming && timing.ttftValue !== null) {
                // If streaming is on, use TTFT from metadata
                duration = timing.ttftValue;
                displayDuration = duration;
                isComplete = true;
            } else if (streaming && timing.ttftTime !== null) {
                // Fall back to calculated TTFT if metadata ttft not available
                duration = timing.ttftTime - timing.startTime;
                displayDuration = duration;
                isComplete = true;
            } else if (!streaming && timing.inferenceTime !== null) {
                // If streaming is off, use inference time
                duration = timing.inferenceTime;
                displayDuration = duration;
                isComplete = true;
            } else {
                // For incomplete responses, just show elapsed time
                duration = currentTime - timing.startTime;
                displayDuration = duration;
                isComplete = streaming ? 
                    (timing.ttftValue !== null || timing.ttftTime !== null) : 
                    timing.inferenceTime !== null;
            }
            
            // For timeline view: first segment starts at 0
            if (index === 0) {
                startPercent = 0;
                widthPercent = Math.min(100, (duration / maxTimeMs) * 100);
                lastEndTime = duration;
            } 
            // Subsequent segments start where the previous ended
            else {
                startPercent = Math.min(100, (lastEndTime / maxTimeMs) * 100);
                // Width is the difference between this completion and the last one
                const segmentDuration = Math.max(0, duration - lastEndTime);
                widthPercent = Math.min(100 - startPercent, (segmentDuration / maxTimeMs) * 100);
                lastEndTime = duration;
            }
            
            return {
                model: timing.model,
                percent: widthPercent,
                startPercent: startPercent,
                color: getModelColor(timing.model),
                duration: displayDuration,
                isComplete: isComplete,
                metricType: streaming ? 'ttft' : 'inference',
                maxTimeMs: maxTimeMs
            };
        });
        
        return segments;
    }, [streaming, currentTime]);
    
    // Get all model timing segments for the stacked progress bar for current turn
    const getModelSegments = useCallback(() => {
        return getModelSegmentsForTimings(modelTimings);
    }, [modelTimings, getModelSegmentsForTimings]);

    const handleMessageClick = (message: SandboxMessage) => {
        if (message.sender === 'BOT') {
            setTargetMessage(message.id === targetMessage?.id ? null : message);
            if (isMobile && openRightSider && message.id !== targetMessage?.id) {
                openRightSider();
            }
        }
    };

    // Helper function to get all messages from conversation
    const getAllMessages = useCallback(() => {
        return conversation.turns.flatMap(turn => turn.messages);
    }, [conversation]);

    // Helper function to get the last turn
    const getLastTurn = useCallback(() => {
        return conversation.turns[conversation.turns.length - 1];
    }, [conversation]);

    // Helper function to update a message in the conversation
    const updateMessageInConversation = useCallback((messageId: number | string, updateFn: (message: SandboxMessage) => SandboxMessage) => {
        setConversation(prevConversation => {
            const newTurns = prevConversation.turns.map(turn => {
                const updatedMessages = turn.messages.map(msg => 
                    msg.id === messageId ? updateFn(msg) : msg
                );
                return { ...turn, messages: updatedMessages };
            });
            return { ...prevConversation, turns: newTurns };
        });
    }, []);

    // Helper function to add a new turn to the conversation
    const addTurn = useCallback((sender: "USER" | "BOT", messages: SandboxMessage[]) => {
        setConversation(prevConversation => ({
            turns: [...prevConversation.turns, { sender, messages }]
        }));
    }, []);

    // Helper function to update the last turn's messages
    const updateLastTurnMessages = useCallback((updateFn: (messages: SandboxMessage[]) => SandboxMessage[]) => {
        setConversation(prevConversation => {
            if (prevConversation.turns.length === 0) return prevConversation;
            
            const newTurns = [...prevConversation.turns];
            const lastTurn = newTurns[newTurns.length - 1];
            newTurns[newTurns.length - 1] = {
                ...lastTurn,
                messages: updateFn(lastTurn.messages)
            };
            return { ...prevConversation, turns: newTurns };
        });
    }, []);

    useEffect(() => {
        if (chosenApplication) {
            setConversation({ turns: [] });
            // Reset processed tokens when starting a new conversation
            processedTokenRefs.current = {};
            newSession();
        }
    }, [chosenApplication]);

    useEffect(() => {
        conversationRef.current = conversation;
        scrollToBottom();
    }, [conversation]);

    useEffect(() => {
        evalResponsesRef.current = evalResponses;
        if (evalResponses.length > 0) {
            handleFaultFound();
        }
    }, [evalResponses]);

    useEffect(() => {
        // Add window resize listener to update isMobile state
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleFaultFound = () => {
        if (!tourShown) {
            window.heap.track('Tour Shown - Fault');
            setTourShown(true);
            setTourOpen(true);
        }
    };

    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    const newSession = () => {
        if (chosenApplication && chosenApplication.id) {
            setLoading(true);
            const session: SandboxSession = {
                sandbox_application_id: chosenApplication.id,
            };
            
            // Use the streaming session endpoint
            const subscription = SandboxService.newSessionStream(session, apiKey).subscribe(
                (response: any) => {                    
                    if (response.type === 'session') {
                        // Set the session from the response
                        setSession(response.data);
                        
                        // Track session creation
                        window.heap.track('New Session', { 'session_id': response.data.id });
                    }
                    else if (response.type === 'bot_message') {
                        // Add the bot message to the state
                        setConversation((prevConversation) => ({
                            turns: [
                                ...prevConversation.turns,
                                { sender: 'BOT', messages: [response.data] }
                            ]
                        }));
                        
                        // No longer awaiting the first message
                        setAwaitingFirstMessage(false);

                        setLoading(false);
                        
                        // Track bot message event
                        window.heap.track('New Message', { 
                            'sender': 'BOT', 
                            'message': response.data.message, 
                            'session_id': response.data.sandbox_session_id
                        });
                    }
                    else if (response.type === 'token_update') {
                        // Use the common handler for token updates
                        const updateFn = handleTokenUpdate(response.data, session?.id ? String(session.id) : undefined);
                        if (updateFn) {
                            setAwaitingFirstMessage(false);
                            setLoading(false);
                            setIsTyping(false);
                        }
                    }
                    else if (response.type === 'evaluation') {
                        // Handle evaluation responses directly
                        
                        // Find the associated message by reference_id
                        const evalResponse = response.data;
                        
                        // Search through all turns and messages to find the matching message
                        let foundMessage = false;
                        let hasFault = false;
                        
                        setConversation(prevConversation => {
                            const newTurns = prevConversation.turns.map(turn => {
                                const messageInTurn = turn.messages.find(msg => {
                                    const matches = String(msg.meta?.reference_id) === String(evalResponse?.reference_id);
                                    return matches;
                                });
                                
                                if (messageInTurn) {
                                    foundMessage = true;
                                    console.log(`[SyncChatComponent] Found message to evaluate in turn`);
                                    
                                    const evaluation_results_set: EvaluateResult[] = evalResponse.evaluation_results || [];
                                    
                                    // Create updated message with evaluation results
                                    const updatedMessage = { ...messageInTurn };
                                    if (evalResponse.correction && updatedMessage.meta) {
                                        updatedMessage.meta = { ...updatedMessage.meta, correction: evalResponse.correction };
                                    }
                                    
                                    for (const eval_result of evaluation_results_set) {
                                        if (eval_result.meta && typeof eval_result.meta === 'string') {
                                            eval_result.meta = JSON.parse(eval_result.meta);
                                        }
                                        if (eval_result?.status === 'FAULT') {
                                            hasFault = true;
                                            console.log(`[SyncChatComponent] Found fault in evaluation`);
                                            
                                            window.heap.track('Fault Found', { 
                                                'session_id': session?.id, 
                                                'sentinel': eval_result.sentinel_name, 
                                                'severity': eval_result.severity?.toString() || '', 
                                                'fault_description': eval_result.description,
                                                'fault_correction': eval_result.meta?.correction || '', 
                                                'associated_message': updatedMessage.message 
                                            });
                                            
                                            if (updatedMessage.meta && Array.isArray(updatedMessage.meta.eval_results)) {
                                                updatedMessage.meta.eval_results = [...updatedMessage.meta.eval_results, eval_result];
                                                updatedMessage.meta.fault_found = true;
                                            } else {
                                                updatedMessage.meta = {
                                                    ...updatedMessage.meta,
                                                    eval_results: [eval_result],
                                                    fault_found: true
                                                };
                                            }
                                        }
                                    }
                                    
                                    if (!hasFault && !updatedMessage.meta?.eval_results) {
                                        console.log(`[SyncChatComponent] No fault found, adding eval_result with lowest eval_time`);
                                        const eval_result = evaluation_results_set.sort((a, b) => (a.eval_time || 0) - (b.eval_time || 0))[0];
                                        updatedMessage.meta = {
                                            ...updatedMessage.meta,
                                            eval_results: [eval_result],
                                            fault_found: false
                                        };
                                    }
                                    
                                    return {
                                        ...turn,
                                        messages: turn.messages.map(msg =>
                                            String(msg.meta?.reference_id) === String(evalResponse?.reference_id)
                                                ? updatedMessage
                                                : msg
                                        )
                                    };
                                }
                                return turn;
                            });
                            
                            return { ...prevConversation, turns: newTurns };
                        });
                        
                        const strobeColor = hasFault ? 'red' : 'green';
                        setStrobeMessage({ id: String(evalResponse?.reference_id), color: strobeColor });
                        setTimeout(() => setStrobeMessage({ id: null, color: '' }), 5000);
                        
                        if (hasFault) {
                            console.log(`[SyncChatComponent] Adding fault to evalResponses`);
                            setEvalResponses((prevEvalResponses: EvaluateResponse[]) => {
                                const exists = prevEvalResponses.some(er => 
                                    er.reference_id === evalResponse.reference_id
                                );
                                
                                if (!exists) {
                                    return [...prevEvalResponses, evalResponse];
                                }
                                return prevEvalResponses;
                            });
                        }
                    }
                },
                (error: any) => {
                    console.error('Error in session stream:', error);
                    setLoading(false);
                },
                () => {
                    // Stream completed
                    console.log('[SyncChatComponent] Session stream completed');
                }
            );
            
            // Return the subscription for cleanup if needed
            return subscription;
        }
    };

    // Add helper function for number spacing
    const addNumberSpacing = (text: string | null | undefined): string => {
        if (!text) return "";
        // Add space before a number that follows a letter
        text = text.replace(/([a-zA-Z])(\d)/g, "$1 $2");
        // Add space after a number that precedes a letter
        text = text.replace(/(\d)([a-zA-Z])/g, "$1 $2");
        return text;
    };

    // Helper function to handle token updates consistently
    const handleTokenUpdate = useCallback((tokenUpdate: SandboxMessage, sessionId: string | undefined) => {
        if (!tokenUpdate.message || tokenUpdate.message.trim() == "") {
            console.log("[SyncChatComponent] Skipping empty token update");
            return null;
        }

        // Create a unique token identifier to prevent duplicate processing
        const tokenId = `${tokenUpdate.meta?.reference_id}_${tokenUpdate.meta?.token_id || Date.now()}`;
        
        // Skip if we've already processed this exact token
        if (processedTokenRefs.current[tokenId]) {
            return null;
        }
        
        // Mark this token as processed
        processedTokenRefs.current[tokenId] = true;

        // Only apply number spacing for Red Robin model
        const modelName = tokenUpdate.meta?.model as string || "";
        
        setConversation(prevConversation => {
            const turns = [...prevConversation.turns];
            const lastTurn = turns[turns.length - 1];
            const newTokenMessage = modelName.toLowerCase().includes("red_robin") ? addNumberSpacing(tokenUpdate.message) : tokenUpdate.message
            
            // Check if there's already a bot turn with this reference_id
            if (lastTurn && lastTurn.sender === "BOT") {
                const existingMessageIndex = lastTurn.messages.findIndex(
                    msg => msg.meta?.reference_id === tokenUpdate.meta?.reference_id
                );
                
                if (existingMessageIndex >= 0) {
                    // Update existing message with new token
                    const updatedMessages = [...lastTurn.messages];
                    const existingMessage = updatedMessages[existingMessageIndex];
                    const newMessage = (existingMessage.message || "") + newTokenMessage
                    updatedMessages[existingMessageIndex] = {
                        ...existingMessage,
                        message: modelName.toLowerCase().includes("red_robin") ? addNumberSpacing(newMessage) : newMessage,
                        meta: {
                            ...existingMessage.meta,
                            ...tokenUpdate.meta,
                            streaming: true
                        }
                    };
                    turns[turns.length - 1] = { ...lastTurn, messages: updatedMessages };
                } else {
                    // Add new message to existing BOT turn
                    turns[turns.length - 1] = {
                        ...lastTurn,
                        messages: [...lastTurn.messages, {
                            ...tokenUpdate,
                            message: newTokenMessage,
                            meta: {
                                ...tokenUpdate.meta,
                                streaming: true
                            }
                        }]
                    };
                }
            } else {
                // Create new BOT turn
                turns.push({
                    sender: "BOT",
                    messages: [{
                        ...tokenUpdate,
                        message: newTokenMessage,
                        meta: {
                            ...tokenUpdate.meta,
                            streaming: true
                        }
                    }]
                });
            }
            
            return { ...prevConversation, turns };
        });
    }, []);

    const handleSend = (override: string | null = null) => {
        const tmp = override || inputValue;
        if (!tmp.trim()) return;
    
        // Reset model timings for new message
        setModelTimings([]);
    
        // Create a temporary new message with a temporary ID
        const tempId = Date.now();
        const tempNewMessage: SandboxMessage = {
            id: tempId,
            message: tmp,
            sender: 'USER',
            sandbox_session_id: session?.id,
        };
    
        // Add the temporary message to the conversation
        addTurn('USER', [tempNewMessage]);
        setInputValue('');
        setIsTyping(true);

        // Ensure we have at least one model to process
        if (!models || models.length === 0) {
            console.error('[SyncChatComponent] No models selected');
            setIsTyping(false);
            return;
        }

        // Process each model in sequence
        models.forEach(model => {
            handleSendForModel(model, tmp, tempId);
        });
    };

    const handleSendForModel = (model: string, tmp: string, tempId: number) => {
        // Format previous messages to include only relevant data and only messages from the current model or user
        const cleanedMessages = conversation.turns.reduce((acc: SandboxMessage[], turn, turnIndex) => {
            const relevantMessages = turn.messages.filter(msg => 
                msg.sender === 'USER' || // Always include user messages
                msg.meta?.model === model || // Include messages from current model
                (turnIndex === 0 && turn.sender === 'BOT') // Include first bot turn (greeting)
            );
            return [...acc, ...relevantMessages];
        }, []).map(msg => ({
            id: msg.id,
            message: msg.message,
            sender: msg.sender,
            sandbox_session_id: msg.sandbox_session_id,
            meta: msg.meta
        }));

        // Send the message to the server without the temporary ID
        const messageToSend: Omit<SandboxMessage, 'id'> = {
            message: tmp,
            sender: 'USER',
            sandbox_session_id: session?.id,
            sandbox_application_id: session?.sandbox_application_id,
            session_token: session?.token,
            company_id: chosenApplication?.company_id,
            meta: {
                model: model,
                temperature: temperature,
                evaluations: evaluations,
                autocorrect: autocorrect,
                retrieve_context: retrieveContext,
                streaming: streaming,
                client_start_time: Date.now()
            }
        };

        const messageCount = cleanedMessages.length;
        window.heap.track('New Message', { 'sender': 'USER', 'message': tmp, 'session_id': session?.id });

        // Add model timing entry when sending message
        setModelTimings(prev => [...prev, { 
            model, 
            startTime: Date.now(), 
            ttftTime: null,
            ttftValue: null,
            completionTime: null,
            inferenceTime: null
        }]);

        const subscription = SandboxService.newMessageStream(messageToSend, cleanedMessages, apiKey).subscribe(
            (response: any) => {

                var client_first_token_time = -1;
                if (response.type === 'user_message') {
                    // Replace the temporary user message with the real one
                    updateMessageInConversation(tempId, () => response.data);
                    
                    window.heap.track('User Message Stored', { 
                        'message_id': response.data.id, 
                        'session_id': session?.id 
                    });
                } 
                else if (response.type === 'token_update') {
                    if (client_first_token_time === -1) {
                        client_first_token_time = Date.now();
                        
                        // Update model timing with TTFT time (but don't set ttftValue yet)
                        const modelName = response.data?.meta?.model as string || "";
                        
                        setModelTimings(prev => prev.map(timing => 
                            timing.model === modelName && timing.ttftTime === null
                                ? { 
                                    ...timing, 
                                    ttftTime: client_first_token_time
                                }
                                : timing
                        ));
                    }
                    handleTokenUpdate(response.data, session?.id ? String(session.id) : undefined);
                    setIsTyping(false);
                }
                else if (response.type === 'bot_message') {
                    // This should be the final complete message after streaming is done
                    const botMessage = response.data;
                    botMessage.meta["client_end_time"] = Date.now();
                    if (botMessage.meta?.ttft_diff) {
                        botMessage.meta["client_end_time"] = botMessage.meta["client_end_time"] - botMessage.meta["ttft_diff"];
                    }
                    // Update model timing with completion time and inference time
                    const modelName = botMessage.meta?.model as string || "";
                    const infTime = botMessage.meta?.inf_time as number;
                    const ttftFromMeta = botMessage.meta?.ttft as number;
                    
                    setModelTimings(prev => prev.map(timing => 
                        timing.model === modelName && timing.completionTime === null
                            ? { 
                                ...timing, 
                                completionTime: Date.now(),
                                inferenceTime: infTime || null,
                                ttftValue: ttftFromMeta || null
                              }
                            : timing
                    ));
                    
                    setConversation(prevConversation => {
                        const turns = [...prevConversation.turns];
                        const lastTurn = turns[turns.length - 1];
                        
                        if (lastTurn && lastTurn.sender === 'BOT') {
                            const existingMessageIndex = lastTurn.messages.findIndex(
                                msg => msg.meta?.reference_id === botMessage.meta?.reference_id
                            );
                            
                            if (existingMessageIndex >= 0) {
                                const updatedMessages = [...lastTurn.messages];
                                const existingMessage = updatedMessages[existingMessageIndex];
                                updatedMessages[existingMessageIndex] = {
                                    ...existingMessage,
                                    ...botMessage,
                                    message: existingMessage.message || botMessage.message || "",
                                    meta: {
                                        ...existingMessage.meta,
                                        ...botMessage.meta,
                                        streaming: false
                                    }
                                };
                                turns[turns.length - 1] = { ...lastTurn, messages: updatedMessages };
                            } else {
                                turns[turns.length - 1] = {
                                    ...lastTurn,
                                    messages: [...lastTurn.messages, {
                                        ...botMessage,
                                        meta: {
                                            ...botMessage.meta,
                                            streaming: false
                                        }
                                    }]
                                };
                            }
                        } else {
                            turns.push({
                                sender: 'BOT',
                                messages: [{
                                    ...botMessage,
                                    meta: {
                                        ...botMessage.meta,
                                        streaming: false
                                    }
                                }]
                            });
                        }
                        
                        return { ...prevConversation, turns };
                    });
                    
                    setIsTyping(false);
                    window.heap.track('New Message', { 
                        'sender': 'BOT', 
                        'message': botMessage.message, 
                        'session_id': session?.id
                    });
                }
                else if (response.type === 'evaluation') {
                    const evalResponse = response.data;                    
                    // Search directly in the conversation for the message
                    setConversation(prevConversation => {
                        let foundMessage = false;
                        const newTurns = prevConversation.turns.map(turn => {
                            const messageInTurn = turn.messages.find(msg => 
                                String(msg.meta?.reference_id) === String(evalResponse?.reference_id)
                            );
                            
                            if (messageInTurn) {
                                foundMessage = true;
                                console.log(`[SyncChatComponent] Found message to evaluate in conversation`);
                                
                                const evaluation_results_set: EvaluateResult[] = evalResponse.evaluation_results || [];
                                let hasFault = false;
                                
                                // Create updated message with evaluation results
                                const updatedMessage = { ...messageInTurn };
                                if (evalResponse.correction && updatedMessage.meta) {
                                    updatedMessage.meta = { ...updatedMessage.meta, correction: evalResponse.correction };
                                }
                                
                                for (const eval_result of evaluation_results_set) {
                                    if (eval_result.meta && typeof eval_result.meta === 'string') {
                                        eval_result.meta = JSON.parse(eval_result.meta);
                                    }
                                    if (eval_result?.status === 'FAULT') {
                                        hasFault = true;
                                        console.log(`[SyncChatComponent] Found fault in evaluation`);
                                        
                                        window.heap.track('Fault Found', { 
                                            'session_id': session?.id, 
                                            'sentinel': eval_result.sentinel_name, 
                                            'severity': eval_result.severity?.toString() || '', 
                                            'fault_description': eval_result.description,
                                            'fault_correction': eval_result.meta?.correction || '', 
                                            'associated_message': updatedMessage.message 
                                        });
                                        
                                        if (updatedMessage.meta && Array.isArray(updatedMessage.meta.eval_results)) {
                                            updatedMessage.meta.eval_results = [...updatedMessage.meta.eval_results, eval_result];
                                            updatedMessage.meta.fault_found = true;
                                        } else {
                                            updatedMessage.meta = {
                                                ...updatedMessage.meta,
                                                eval_results: [eval_result],
                                                fault_found: true
                                            };
                                        }
                                    }
                                }
                                
                                if (!hasFault && !updatedMessage.meta?.eval_results) {
                                    console.log(`[SyncChatComponent] No fault found, adding eval_result with lowest eval_time`);
                                    const eval_result = evaluation_results_set.sort((a, b) => (a.eval_time || 0) - (b.eval_time || 0))[0];
                                    updatedMessage.meta = {
                                        ...updatedMessage.meta,
                                        eval_results: [eval_result],
                                        fault_found: false
                                    };
                                }
                                
                                const strobeColor = hasFault ? 'red' : 'green';
                                setStrobeMessage({ id: String(evalResponse?.reference_id), color: strobeColor });
                                setTimeout(() => setStrobeMessage({ id: null, color: '' }), 5000);
                                
                                if (hasFault) {
                                    setEvalResponses((prevEvalResponses: EvaluateResponse[]) => {
                                        const exists = prevEvalResponses.some(er => 
                                            er.reference_id === evalResponse.reference_id
                                        );
                                        
                                        if (!exists) {
                                            return [...prevEvalResponses, evalResponse];
                                        }
                                        return prevEvalResponses;
                                    });
                                }
                                
                                return {
                                    ...turn,
                                    messages: turn.messages.map(msg =>
                                        String(msg.meta?.reference_id) === String(evalResponse?.reference_id)
                                            ? updatedMessage
                                            : msg
                                    )
                                };
                            }
                            return turn;
                        });
                        
                        
                        return { ...prevConversation, turns: newTurns };
                    });
                }
            },
            (error: any) => {
                console.error('Error in message stream:', error);
                setIsTyping(false);
            },
            () => {
                console.log('[SyncChatComponent] Message stream completed');
                setIsTyping(false);
            }
        );

        return subscription;

    };

    useImperativeHandle(ref, () => ({
        sendFaultyMessage,
        clearConversation: () => {
            setConversation({ turns: [] });
            // Reset processed tokens
            processedTokenRefs.current = {};
            // Reset model timings
            setModelTimings([]);
            // Reset all turn timings
            setAllTurnTimings([]);
            // Start new session
            newSession();
            // Clear eval responses
            setEvalResponses([]);
            // Clear target message
            setTargetMessage(null);
            // Track refresh event
            window.heap.track('Session Refreshed');
        }
    }));

    const sendFaultyMessage = (sentinel: Sentinel) => {
        const msg = `<${sentinel.sentinel_name}>`;
        handleSend(msg);
    };

    const applyEvaluation = (messages: SandboxMessage[], evalResponse: EvaluateResponse) => {
        
        // Search through all turns and messages to find the matching message
        let foundMessage = false;
        let hasFault = false;
        
        setConversation(prevConversation => {
            const newTurns = prevConversation.turns.map(turn => {
                const messageInTurn = turn.messages.find(msg => {
                    const matches = String(msg.meta?.reference_id) === String(evalResponse?.reference_id);
                    return matches;
                });
                
                if (messageInTurn) {
                    foundMessage = true;
                    console.log(`[SyncChatComponent] Found message to evaluate in turn`);
                    
                    const evaluation_results_set: EvaluateResult[] = evalResponse.evaluation_results || [];
                    
                    // Create updated message with evaluation results
                    const updatedMessage = { ...messageInTurn };
                    if (evalResponse.correction && updatedMessage.meta) {
                        updatedMessage.meta = { ...updatedMessage.meta, correction: evalResponse.correction };
                    }
                    
                    for (const eval_result of evaluation_results_set) {
                        if (eval_result.meta && typeof eval_result.meta === 'string') {
                            eval_result.meta = JSON.parse(eval_result.meta);
                        }
                        if (eval_result?.status === 'FAULT') {
                            hasFault = true;
                            console.log(`[SyncChatComponent] Found fault in evaluation`);
                            
                            window.heap.track('Fault Found', { 
                                'session_id': session?.id, 
                                'sentinel': eval_result.sentinel_name, 
                                'severity': eval_result.severity?.toString() || '', 
                                'fault_description': eval_result.description,
                                'fault_correction': eval_result.meta?.correction || '', 
                                'associated_message': updatedMessage.message 
                            });
                            
                            if (updatedMessage.meta && Array.isArray(updatedMessage.meta.eval_results)) {
                                updatedMessage.meta.eval_results = [...updatedMessage.meta.eval_results, eval_result];
                                updatedMessage.meta.fault_found = true;
                            } else {
                                updatedMessage.meta = {
                                    ...updatedMessage.meta,
                                    eval_results: [eval_result],
                                    fault_found: true
                                };
                            }
                        }
                    }
                    
                    if (!hasFault && !updatedMessage.meta?.eval_results) {
                        console.log(`[SyncChatComponent] No fault found, adding eval_result with lowest eval_time`);
                        const eval_result = evaluation_results_set.sort((a, b) => (a.eval_time || 0) - (b.eval_time || 0))[0];
                        updatedMessage.meta = {
                            ...updatedMessage.meta,
                            eval_results: [eval_result],
                            fault_found: false
                        };
                    }
                    
                    return {
                        ...turn,
                        messages: turn.messages.map(msg =>
                            String(msg.meta?.reference_id) === String(evalResponse?.reference_id)
                                ? updatedMessage
                                : msg
                        )
                    };
                }
                return turn;
            });
            
            return { ...prevConversation, turns: newTurns };
        });
        
        const strobeColor = hasFault ? 'red' : 'green';
        setStrobeMessage({ id: String(evalResponse?.reference_id), color: strobeColor });
        setTimeout(() => setStrobeMessage({ id: null, color: '' }), 5000);
        
        if (hasFault) {
            console.log(`[SyncChatComponent] Adding fault to evalResponses`);
            setEvalResponses((prevEvalResponses: EvaluateResponse[]) => {
                const exists = prevEvalResponses.some(er => 
                    er.reference_id === evalResponse.reference_id
                );
                
                if (!exists) {
                    return [...prevEvalResponses, evalResponse];
                }
                return prevEvalResponses;
            });
        }
    };

    return (
        <div className="chatContainer" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="messagesContainer" ref={messagesContainerRef} style={{ position: 'relative' }}>
                {(loading || awaitingFirstMessage) && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                        <Spin size="large" />
                    </div>
                )}
                {!chosenApplication && !loading && conversation.turns.length === 0 &&
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                        <Typography style={{fontSize: '18px', color: 'rgba(0,0,0,0.2)', textAlign: 'center'}}>Choose an application to start a chat</Typography>
                    </div>
                }
                {!loading && (
                    <>
                        {conversation.turns.map((turn, turnIndex) => (
                            <div key={turnIndex} className="turn">
                                {turn.sender === 'USER' ? (
                                    // Render user messages as before
                                    turn.messages.filter(message => message.message && message.message.trim() !== "").map((message, messageIndex) => (
                                        <div key={message?.meta?.reference_id as string}>
                                            <div 
                                                className={`message ${strobeMessage?.id == String(message?.id) ? `strobe-${strobeMessage?.color}` : ''}`} 
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'flex-start',
                                                    marginLeft: message.id === targetMessage?.id ? '20px' : '0',
                                                    background: message.id === targetMessage?.id ? '#fff' : 'rgb(255, 255, 255, 0.4)'
                                                }}
                                                onClick={() => handleMessageClick(message)}
                                                data-sender={message.sender}
                                            >
                                                <div className="message-avatar">
                                                    <Avatar icon={<UserOutlined style={{ color: 'black' }} />} style={{ background: 'white', border: '1px solid rgba(0,0,0,0.1)' }} />
                                                </div>
                                                <div className="message-content">
                                                    <div className="message-header" style={{ display: 'flex', alignItems: 'center' }}>
                                                        <strong>You</strong>
                                                    </div>
                                                    <div>{typeof message.message === 'string' ? message.message : JSON.stringify(message.message)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    // Modified bot message rendering with enhanced visual distinction
                                    <div className="message" style={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start',
                                        background: 'rgb(255, 255, 255, 0.4)'
                                    }}>
                                        <div className="message-avatar">
                                            <Avatar src={MaitaiIcon} style={{ padding: '5px', background: 'white', border: '1px solid rgba(0,0,0,0.1)'}} />
                                        </div>
                                        <div className="message-content" style={{ width: '100%' }}>
                                            <div className="message-header">
                                                <strong>Maitai Bot</strong>
                                            </div>
                                            
                                            {/* Performance Bar - Show for all bot turns */}
                                            {(() => {
                                                // For current turn, use modelTimings
                                                if (turnIndex === conversation.turns.length - 1 && modelTimings.length > 0) {
                                                    return renderPerformanceBar(modelTimings, getModelSegments(), getModelLabel);
                                                }
                                                
                                                // For past turns, use stored timings from allTurnTimings
                                                const turnTiming = allTurnTimings.find(t => t.turnIndex === turnIndex);
                                                if (turnTiming && turnTiming.modelTimings.length > 0) {
                                                    const segments = getModelSegmentsForTimings(turnTiming.modelTimings);
                                                    return renderPerformanceBar(turnTiming.modelTimings, segments, getModelLabel);
                                                }
                                                
                                                // No timings available for this turn
                                                return null;
                                            })()}
                                            
                                            <div style={{ 
                                                display: 'flex', 
                                                flexDirection: isMobile || models.length <= 1 || turnIndex === 0 ? 'column' : 'row', 
                                                flexWrap: isMobile || models.length <= 1 || turnIndex === 0 ? 'nowrap' : 'wrap',
                                                gap: '16px'
                                            }}>
                                            {turn.messages.filter(message => message.message && message.message.trim() !== "").map((message, messageIndex) => {
                                                const modelName = message.meta?.model as string || "";
                                                const modelColor = getModelColor(modelName);
                                                const ttft = message.meta?.ttft as number;
                                                const infTime = message.meta?.inf_time as number;
                                                const useHorizontalLayout = !isMobile && models.length > 1 && turnIndex !== 0;
                                                const flexBasis = useHorizontalLayout 
                                                    ? `calc(${100 / models.length}% - ${(models.length - 1) * 16 / models.length}px)` 
                                                    : '100%';
                                                
                                                return (
                                                    <div 
                                                        key={message?.meta?.reference_id as string}
                                                        className={`bot-response ${strobeMessage?.id == String(message?.id) ? `strobe-${strobeMessage?.color}` : ''}`}
                                                        style={{ 
                                                            marginTop: (isMobile || models.length <= 1 || turnIndex === 0) && messageIndex > 0 ? '16px' : '0',
                                                            marginLeft: useHorizontalLayout ? '0' : (message.id === targetMessage?.id ? '20px' : '0'),
                                                            borderTop: (isMobile || models.length <= 1 || turnIndex === 0) && messageIndex > 0 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                                                            paddingTop: (isMobile || models.length <= 1 || turnIndex === 0) && messageIndex > 0 ? '16px' : '0',
                                                            flex: useHorizontalLayout ? `1 1 ${flexBasis}` : '1 0 100%',
                                                            maxWidth: useHorizontalLayout ? flexBasis : '100%',
                                                            border: useHorizontalLayout ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                                                            borderRadius: useHorizontalLayout ? '8px' : '0',
                                                            padding: useHorizontalLayout ? '12px' : '0',
                                                            background: message.id === targetMessage?.id ? '#fff' : 'transparent',
                                                        }}
                                                        onClick={() => handleMessageClick(message)}
                                                    >
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '0px 8px 0px 6px',
                                                            marginBottom: '6px',
                                                            borderLeft: `3px solid ${modelColor}`
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <span style={{ 
                                                                    fontWeight: 'bold', 
                                                                    color: modelColor
                                                                }}>
                                                                    {getModelLabel(modelName)}
                                                                </span>
                                                                
                                                                {message.meta && message.meta?.is_correction ? (
                                                                    <span style={{ marginLeft: '8px', fontWeight: 'normal', color: '#ff4d4f' }}>CORRECTION</span>
                                                                ) : message.sender === 'BOT' && message.meta?.fault_found === false ? (
                                                                    <CheckCircleOutlined style={{ color: 'green', marginLeft: '8px' }} />
                                                                ) : null}
                                                            </div>
                                                            
                                                            {/* Timing information */}
                                                            {(ttft || infTime) && (
                                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <ClockCircleOutlined style={{ marginRight: '4px', fontSize: '12px', color: 'rgba(0,0,0,0.45)' }} />
                                                                    <Tooltip title={ttft ? "Time to first token / Inference time" : "Inference time"}>
                                                                        <Tag style={{ margin: 0 }}>
                                                                            {ttft ? (
                                                                                <>
                                                                                    <span style={{ color: getTimingColor(ttft, true) }}>{ttft}</span>
                                                                                    <span style={{ color: 'rgba(0,0,0,0.45)' }}>/</span>
                                                                                </>
                                                                            ) : null}
                                                                            <span style={{ color: getTimingColor(infTime || 0, false) }}>{infTime || 0}</span>
                                                                            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: '11px' }}> ms</span>
                                                                        </Tag>
                                                                    </Tooltip>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        {Boolean(message.meta && Array.isArray((message.meta as any).eval_results) && (message.meta as any).fault_found) && (
                                                            <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'start', marginBottom: '8px' }}>
                                                                {((message.meta as any).eval_results as EvaluateResult[]).map((eval_result: EvaluateResult, index: number) => (
                                                                    eval_result.sentinel_name ?
                                                                        <Tooltip 
                                                                            key={index}
                                                                            overlayClassName="custom-tooltip"
                                                                            title={
                                                                                <Descriptions size="small" bordered column={1}>
                                                                                    <Descriptions.Item label="Description">{eval_result.description}</Descriptions.Item>
                                                                                    <Descriptions.Item label="Correction">{typeof eval_result.meta?.correction === 'string' ? `"${eval_result.meta.correction}"` : ''}</Descriptions.Item>
                                                                                </Descriptions>
                                                                            }
                                                                        >
                                                                            <Tag 
                                                                                color={severityColor[eval_result.severity || 1]} 
                                                                                style={{ marginLeft: '8px' }}
                                                                                ref={eval_result.status === 'FAULT' ? faultRef : null}
                                                                            >
                                                                                {eval_result.sentinel_name}
                                                                            </Tag>
                                                                        </Tooltip>
                                                                    : null
                                                                ))}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Add back the correction/uncorrection tag */}
                                                        {message.sender === 'BOT' && (message.meta as any)?.eval_results && (message.meta as any)?.fault_found && (
                                                            <div style={{ marginBottom: '8px' }}>
                                                                <Tooltip title={message.meta?.original ? `Original: "${message.meta.original}"` : "Output has faults that were not corrected."}>
                                                                    <Tag color={message.meta?.original ? 'success' : 'error'} style={{ marginLeft: '0px' }}>
                                                                        {message.meta?.original ? 'Corrected' : 'Uncorrected'}
                                                                    </Tag>
                                                                </Tooltip>
                                                            </div>
                                                        )}
                                                        
                                                        <div>{typeof message.message === 'string' ? message.message : JSON.stringify(message.message)}</div>
                                                    </div>
                                                );
                                            })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {isTyping && (
                            <div className="typing-indicator">
                                <span className="dot"></span>
                                <span className="dot"></span>
                                <span className="dot"></span>
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="inputContainer">
                <Input placeholder="Type a message..." value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                  onPressEnter={() => handleSend(null)} disabled={chosenApplication === undefined || session === undefined}/>
                <Button type="primary" onClick={() => handleSend(null)} disabled={chosenApplication === undefined || session === undefined}>Send</Button>
            </div>
            <Tour open={tourOpen} onClose={() => setTourOpen(false)} steps={tourSteps} />
        </div>
    );
});

// Helper function to render the performance bar
const renderPerformanceBar = (timings: ModelTiming[], segments: any[], getModelLabelFn: (modelName: string) => React.ReactNode) => {
    if (timings.length === 0 || segments.length === 0) return null;
    
    // Get the max time value from the first segment (all segments share the same maxTimeMs)
    const maxTimeMs = segments[0]?.maxTimeMs || 500;
    
    return (
        <div style={{ 
            margin: '8px 0 12px 0'
        }}>
            {/* Stacked progress bar */}
            <div style={{ 
                height: '12px', 
                width: '100%', 
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '3px',
                overflow: 'hidden',
                display: 'flex',
                position: 'relative'
            }}>
                {/* 250ms threshold line */}
                {maxTimeMs > 250 && (
                    <div 
                        style={{
                            position: 'absolute',
                            left: `${(250 / maxTimeMs) * 100}%`,
                            top: 0,
                            height: '100%',
                            width: '2px',
                            background: 'rgba(255, 0, 0, 0.7)',
                            zIndex: 2,
                            boxShadow: '0 0 2px rgba(255, 0, 0, 0.5)'
                        }}
                    />
                )}
                
                {segments.map((segment, idx) => (
                    <Tooltip 
                        key={`${segment.model}-${idx}`}
                        title={
                            <>
                                {getModelLabelFn(segment.model)}
                                <br/>
                                {segment.isComplete 
                                    ? `${segment.metricType === 'ttft' ? 'Time to first token' : 'Inference time'}: ${segment.duration}ms` 
                                    : segment.metricType === 'ttft' ? "Waiting for first token..." : "Waiting for inference time..."}
                            </>
                        }
                    >
                        <div 
                            style={{
                                width: `${segment.percent}%`,
                                height: '100%',
                                background: segment.color,
                                position: 'absolute',
                                left: `${segment.startPercent}%`,
                                transition: 'width 0.3s ease, left 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                opacity: segment.isComplete ? 1 : 0.7,
                                animation: !segment.isComplete ? 'pulse 1.5s infinite' : 'none'
                            }}
                        >
                            {segment.percent > 15 && (
                                <span style={{
                                    color: '#fff',
                                    fontSize: '8px',
                                    fontWeight: 'bold',
                                    textShadow: '0 0 2px rgba(0,0,0,0.5)',
                                    padding: '0 3px',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    lineHeight: '1',
                                    transform: 'translateY(-0.5px)'
                                }}>
                                    {segment.model.split(':').pop()}
                                </span>
                            )}
                        </div>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
};

export default SyncChatComponent; 